/**
 * InspectionEventDrawer.
 *
 * Slide-in form for recording inspection events on stages 9, 11, 13.
 *
 * Two distinct modes:
 *   - "create": no existing event for this stage. User picks date, outcome,
 *     location (for 9/11), notes. Resolved date is auto-derived (ACCEPTED →
 *     null; non-ACCEPTED → optional, may be filled now or later).
 *   - "backfill": an event exists but its outcome was non-ACCEPTED and the
 *     resolvedDate was deferred. User fills it in, optionally adjusting
 *     notes. We surface the original event date as read-only context.
 *
 * Stage 13 hides the location field (functional / re-functional check is always on-site).
 * Stages 9 and 11 default location from the project's defaultInspectionLocation.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { todayPktIsoDate } from '@procurement/shared';
import { Drawer } from '@/components/ui/drawer';
import { AttachmentBadge } from '@/components/attachments/attachment-badge';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { cn } from '@/lib/cn';
import {
  useCreateInspectionEvent,
  useUpdateInspectionEvent,
  type InspectionEvent,
  type InspectionLocation,
  type InspectionOutcome,
  type InspectionStageNumber,
} from '@/lib/pipeline-queries';
import type { Project } from '@/lib/project-queries';

interface InspectionEventDrawerProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  itemId: string;
  /** Project context — for defaultInspectionLocation pre-fill. */
  project: Project;
  /** Which stage this drawer is acting on. */
  stage: InspectionStageNumber;
  /** When set: backfill mode. When null: create mode. */
  existingEvent: InspectionEvent | null;
  /** True when creating stage-13 round 2 after a non-accepted Functional Check. */
  isReFunctionalCheck?: boolean;
  /**
   * Patch 4 — count of attachments anchored to this inspection event.
   * Renders an inline badge in the drawer header (only when backfill mode
   * and count > 0; new events have nothing to attach to yet).
   */
  attachmentCount?: number;
}

const OUTCOME_OPTIONS: ReadonlyArray<{ value: InspectionOutcome; label: string }> = [
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'OBSERVATION', label: 'Observation' },
  { value: 'REJECTED', label: 'Rejected' },
];

const LOCATION_OPTIONS: ReadonlyArray<{ value: InspectionLocation; label: string }> = [
  { value: 'OUR_PREMISES', label: 'Firm’s premises' },
  { value: 'CLIENT_PREMISES', label: 'Client premises' },
  { value: 'THIRD_PARTY_PREMISES', label: 'Third party premises' },
  { value: 'TBD', label: 'TBD' },
];

function todayIso(): string {
  // Patch E18 — PKT date default. See replace-batch-modal.tsx for context.
  return todayPktIsoDate();
}

export function InspectionEventDrawer({
  open,
  onClose,
  projectId,
  itemId,
  project,
  stage,
  existingEvent,
  isReFunctionalCheck = false,
  attachmentCount = 0,
}: InspectionEventDrawerProps) {
  const isBackfill = existingEvent !== null;
  const hasLocation = stage !== 13;

  // Form state — reset whenever the drawer opens with a different event/stage
  const [eventDate, setEventDate] = useState<string>('');
  const [outcome, setOutcome] = useState<InspectionOutcome>('ACCEPTED');
  const [resolvedDate, setResolvedDate] = useState<string>('');
  const [location, setLocation] = useState<InspectionLocation>(
    project.defaultInspectionLocation ?? 'OUR_PREMISES',
  );
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (existingEvent) {
      setEventDate(existingEvent.eventDate);
      setOutcome(existingEvent.outcome);
      setResolvedDate(existingEvent.resolvedDate ?? '');
      setLocation(existingEvent.location ?? project.defaultInspectionLocation ?? 'OUR_PREMISES');
      setNotes(existingEvent.notes ?? '');
    } else {
      setEventDate(todayIso());
      setOutcome('ACCEPTED');
      setResolvedDate('');
      setLocation(project.defaultInspectionLocation ?? 'OUR_PREMISES');
      setNotes('');
    }
    setError(null);
  }, [open, existingEvent, project.defaultInspectionLocation]);

  const create = useCreateInspectionEvent(projectId, itemId);
  const update = useUpdateInspectionEvent(projectId, itemId);
  const pending = create.isPending || update.isPending;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    // Client-side validation mirrors the API (eventDate ≤ today, resolved ≥
    // eventDate when set, ACCEPTED forbids resolvedDate, etc). API is the
    // ultimate authority; these are UX shortcuts.
    if (!eventDate) {
      setError('Event date is required');
      return;
    }
    if (eventDate > todayIso()) {
      setError('Event date cannot be in the future');
      return;
    }
    if (outcome === 'ACCEPTED' && resolvedDate) {
      setError('Resolved date is only used for non-Accepted outcomes');
      return;
    }
    if (outcome !== 'ACCEPTED' && resolvedDate && resolvedDate < eventDate) {
      setError('Resolved date must be on or after the event date');
      return;
    }

    try {
      if (isBackfill && existingEvent) {
        await update.mutateAsync({
          eventId: existingEvent.id,
          rowVersion: existingEvent.rowVersion,
          eventDate,
          outcome,
          notes: notes || null,
          resolvedDate: outcome === 'ACCEPTED' ? null : resolvedDate || null,
          location: hasLocation ? location : null,
        });
      } else {
        await create.mutateAsync({
          stageNumber: stage,
          eventDate,
          outcome,
          notes: notes || undefined,
          resolvedDate: outcome === 'ACCEPTED' ? undefined : resolvedDate || undefined,
          location: hasLocation ? location : undefined,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  const stageLabel =
    stage === 9
      ? 'Inspection'
      : stage === 11
        ? 'Re-inspection'
        : isReFunctionalCheck
          ? 'Re-functional Check'
          : 'Functional Check';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <span className="inline-flex items-center gap-2">
          <span>Stage {stage} — {stageLabel}</span>
          {/* Patch 4 — visible only when backfilling an existing event AND there
            * are attachments. New events have no id to anchor to. */}
          {existingEvent && attachmentCount > 0 ? (
            <AttachmentBadge count={attachmentCount} />
          ) : null}
        </span>
      }
      description={
        isBackfill
          ? 'Backfill the resolved date or amend this round.'
          : isReFunctionalCheck
            ? 'Record the re-functional check after the functional check observation/rejection was resolved.'
            : 'Record a new inspection event for this stage.'
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {/* Event date */}
        <div>
          <label className="text-sm font-medium text-primary" htmlFor="ie-date">
            Event date
          </label>
          <DmyDateInput
            id="ie-date"
            maxDate={todayIso()}
            value={eventDate}
            onChange={setEventDate}
            className="mt-1"
          />
        </div>

        {/* Outcome */}
        <div>
          <span className="text-sm font-medium text-primary">Outcome</span>
          <div className="mt-1 flex gap-2">
            {OUTCOME_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setOutcome(opt.value)}
                className={cn(
                  'rounded-sm border px-3 py-1.5 text-sm transition-colors',
                  outcome === opt.value
                    ? 'border-accent bg-accent/10 text-accent-strong'
                    : 'border-border-default bg-surface text-secondary hover:border-border-strong',
                )}
                aria-pressed={outcome === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Location — only for stages 9 and 11 */}
        {hasLocation ? (
          <div>
            <span className="text-sm font-medium text-primary">Location</span>
            <div className="mt-1 flex gap-2">
              {LOCATION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setLocation(opt.value)}
                  className={cn(
                    'rounded-sm border px-3 py-1.5 text-sm transition-colors',
                    location === opt.value
                      ? 'border-accent bg-accent/10 text-accent-strong'
                      : 'border-border-default bg-surface text-secondary hover:border-border-strong',
                  )}
                  aria-pressed={location === opt.value}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Resolved date — only for non-ACCEPTED outcomes */}
        {outcome !== 'ACCEPTED' ? (
          <div>
            <label className="text-sm font-medium text-primary" htmlFor="ie-resolved">
              Resolved date
              <span className="ml-1 text-xs font-normal text-tertiary">
                (required to advance past this stage)
              </span>
            </label>
            <DmyDateInput
              id="ie-resolved"
              minDate={eventDate || undefined}
              maxDate={todayIso()}
              value={resolvedDate}
              onChange={setResolvedDate}
              className="mt-1"
            />
            <p className="mt-1 text-xs text-tertiary">
              Leave blank to record the event now and backfill later.
            </p>
          </div>
        ) : null}

        {/* Notes */}
        <div>
          <label className="text-sm font-medium text-primary" htmlFor="ie-notes">
            Notes
            <span className="ml-1 text-xs font-normal text-tertiary">(optional)</span>
          </label>
          <textarea
            id="ie-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              outcome === 'OBSERVATION'
                ? 'What was observed? What action is required?'
                : outcome === 'REJECTED'
                  ? 'Reason for rejection and remediation plan'
                  : 'Anything noteworthy about this inspection'
            }
            className="mt-1 w-full resize-none rounded-sm border border-border-default bg-surface px-2 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
          />
        </div>

        {error ? (
          <div className="rounded-sm border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger-strong">
            {error}
          </div>
        ) : null}

        {/* Actions footer-style */}
        <div className="mt-2 flex items-center justify-end gap-2 border-t border-border-default pt-3">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded-sm border border-border-default bg-surface px-3 py-1.5 text-sm text-secondary hover:border-border-strong disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending}
            className="rounded-sm bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {pending ? 'Saving…' : isBackfill ? 'Update event' : 'Record event'}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
